import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import CommissionList from './CommissionList';
import { commissionApi } from '../../services/api';

export default function CommissionsDashboard() {
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      setSummaryError(null);
      const result = await commissionApi.getSummary();
      if (result.success) {
        setSummary(result);
      } else {
        setSummaryError(result.error || 'Failed to fetch summary');
      }
    } catch (err) {
      setSummaryError(err.message);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'auto_approved':
        return 'Auto Approved';
      case 'manually_approved':
        return 'Manually Approved';
      case 'declined':
        return 'Declined';
      default:
        return status;
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Commissions
      </Typography>

      {/* Summary Cards */}
      {summaryLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : summaryError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {summaryError}
        </Alert>
      ) : summary ? (
        <Stack direction="row" spacing={{ xs: 1, sm: 2 }} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
          {Object.entries(summary.status_counts || {}).map(([status, count]) => (
            <Chip
              key={status}
              label={`${getStatusLabel(status)}: ${count}`}
              color={
                status === 'pending'
                  ? 'warning'
                  : status.includes('approved')
                  ? 'success'
                  : 'default'
              }
              variant={status === 'pending' ? 'filled' : 'outlined'}
            />
          ))}
        </Stack>
      ) : null}

      {/* Commission List */}
      <CommissionList
        key={refreshKey}
        onRefresh={handleRefresh}
      />
    </Box>
  );
}
